import * as React from 'react'
import { CreateInvoiceList } from '../..'
import { observer } from 'mobx-react-lite'
import { Table, Tooltip, Icon, Tag } from 'antd'
import { Link } from 'react-router'
import { Routes } from '../../../../../constants/Routes'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { inject } from 'mobx-react'
import { Filter } from '../../FilterStore'
import moment from 'moment'
import { Currency } from '../../../../../components/Currency'
import { ColumnProps } from 'antd/lib/table'
import '../../invoiceTable.scss'
import { HelpLinks } from '../../../../../constants/ExternalLinks'

interface Props {
  createInvoiceList: CreateInvoiceList
  invoiceFilter: Filter
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface InvoiceListTableRecord {
  key: string
  id: string
  location: string
  vendor: string | null
  title: JSX.Element
  employee: string
  created: string
  completed: string
  total: JSX.Element
  paidStatus?: JSX.Element
  status: JSX.Element
}

const columns: ColumnProps<InvoiceListTableRecord>[] = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Title',
    dataIndex: 'title',
    key: 'title',
    width: 150,
  },
  {
    title: 'Location',
    dataIndex: 'location',
    key: 'location',
    width: 130,
  },
  {
    title: 'Vendor',
    dataIndex: 'vendor',
    key: 'vendor',
    width: 130,
  },
  {
    title: 'Employee',
    dataIndex: 'employee',
    key: 'employee',
  },
  {
    title: (
      <span>
        <span className='mr-2'>Created</span>
        <Tooltip
          title={
            <div>
              <div>Date when this purchase order was created.</div>
              <div>
                The actual date in which inventory was received can be edited via the 'complete
                date' field.
              </div>
            </div>
          }
        >
          <Icon type='question-circle' />
        </Tooltip>
      </span>
    ),
    dataIndex: 'created',
    key: 'created',
  },
  {
    title: (
      <span>
        <span className='mr-2'>Completed</span>
        <Tooltip
          title={
            <div>
              <div>Date when your inventory arrived.</div>
              <div>Compliance reports will be generated based on this date.</div>
            </div>
          }
        >
          <Icon type='question-circle' />
        </Tooltip>
      </span>
    ),
    dataIndex: 'completed',
    key: 'completed',
  },
  {
    title: 'Total',
    dataIndex: 'total',
    key: 'total',
    align: 'right',
  },
  {
    title: 'Paid status',
    dataIndex: 'paidStatus',
    key: 'paidStatus',
    align: 'right',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    align: 'right',
  },
]

export const InvoiceTable = inject('currentCompanyStore')(
  observer((props: Props & InjectedProps) => {
    const { createInvoiceList, invoiceFilter, currentCompanyStore } = props
    const companyId = currentCompanyStore!.company!.id
    const invoiceListItems = createInvoiceList.paginatedInvoices.data

    const createEditInvoiceEndPoint = (invoiceId: string) => {
      const query = `?limit=${invoiceFilter.limit}&offset=${invoiceFilter.offset}`

      return Routes.EDIT_INVOICE(companyId, invoiceId) + query
    }

    const tableDataSource: InvoiceListTableRecord[] = invoiceListItems.map((invoice) => {
      const { invoiceStatusColor, invoiceStatusName } = createInvoiceList.renderInvoiceStatus(
        invoice.status,
      )
      const { paidStatusName, paidStatusColor } = createInvoiceList.renderPaidStatus(invoice)

      return {
        key: invoice.id,
        id: invoice.customId,
        location: invoice.locationName,
        vendor: invoice.supplierName,
        title: (
          <Link to={createEditInvoiceEndPoint(invoice.id)} onClick={(e) => e.stopPropagation()}>
            {invoice.title}
          </Link>
        ),
        employee: invoice.startingUserName || '-',
        created: moment(invoice.createDate).format('MMM D YYYY'),
        completed: invoice.completeDate ? moment(invoice.completeDate).format('MMM D YYYY') : '-',
        total: <Currency centValue={invoice.total} />,
        paidStatus:
          paidStatusName === undefined ? undefined : (
            <Tag color={paidStatusColor} className='mr-0'>
              {paidStatusName}
            </Tag>
          ),
        status: (
          <Tag color={invoiceStatusColor} className='mr-0'>
            {invoiceStatusName}
          </Tag>
        ),
      }
    })

    return (
      <>
        {invoiceListItems.length === 0 ? (
          <div className='border border-light background-white text-muted'>
            <p>You have not added any purchase orders.</p>
            <p>
              You must first <a href='products'>add products</a>. Learn more about purchase ordering{' '}
              <a href={HelpLinks.PURCHASE_ORDERS} target='_blank' rel='noreferrer'>
                in this support article.
              </a>
            </p>
            <div>Use purchase orders to:</div>

            <ul>
              <li>Track product purchases</li>
              <li>Track product costs and margins</li>
              <li>Remain compliant for cannabis products</li>
            </ul>
          </div>
        ) : (
          <Table
            dataSource={tableDataSource}
            className='no-background-table'
            rowClassName={() => 'background-white'}
            columns={columns}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        )}
      </>
    )
  }),
)
